import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { onSnapshot, collection, doc, updateDoc, getDoc, query, orderBy, deleteDoc, runTransaction, setDoc } from 'firebase/firestore';
import { db, functions } from '../protected/firebase';
import { getStorage, ref as storageRef, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../contexts/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import { FiCheckCircle, FiClock, FiUser, FiHash, FiPhone } from 'react-icons/fi';

export default function KitchenDisplay() {
  const { user, companyId } = useAuth();
  const [preorders, setPreorders] = useState([]);
  const [lastPreorderIds, setLastPreorderIds] = useState([]);
  const [newOrderTimers, setNewOrderTimers] = useState({});
  const [showNewOrder, setShowNewOrder] = useState({});
  const [stallType, setStallType] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [filter, setFilter] = useState('today'); // today | later | preorder
  const [publicId, setPublicId] = useState(null);
  const storage = getStorage();

  const clearNewOrder = useCallback((orderId) => {
    setShowNewOrder(prev => {
      const updated = { ...prev };
      delete updated[orderId];
      return updated;
    });
  }, []);

  useEffect(() => {
    if (!companyId) return;
    const stallRef = doc(db, `companies/${companyId}/companySettings/info`);
    getDoc(stallRef).then((snap) => {
      if (snap.exists()) {
        setStallType(snap.data().stallType || 'non-fnb');
      }
    });
    getDoc(doc(db, `companies/${companyId}`)).then((snap) => {
      if (snap.exists()) {
        setPublicId(snap.data()?.publicId || null);
      }
    });
  }, [companyId]);

  useEffect(() => {
    return () => {
      Object.values(newOrderTimers).forEach(timer => clearTimeout(timer));
    };
  }, [newOrderTimers]);
  

  const listenToPreorders = () => {
    const ref = query(collection(db, `companies/${companyId}/preorders`), orderBy('timestamp', 'asc'));
    return onSnapshot(ref, (snapshot) => {
      const now = new Date();
      // build filtered with normalized timestamp values
      const filtered = snapshot.docs
        .map(doc => {
          const data = doc.data();
          // normalize timestamp to JS Date
          const timestamp = (data.timestamp && typeof data.timestamp.toDate === 'function')
            ? data.timestamp.toDate()
            : (data.timestamp ? new Date(data.timestamp) : now);

          return {
            id: doc.id,
            ...data,
            items: (data.items || []).map(item => ({
              ...item,
              ...(item.type === 'bundle' && item.items && {
                isBundle: true,
                bundleItems: item.items.map(bi => `${bi.name} x${bi.quantity}`).join(', ')
              })
            })),
            timestamp,
            displayTime: (() => {
              const pad = (v) => String(v).padStart(2, '0');
              const datePart = `${pad(timestamp.getDate())}/${pad(timestamp.getMonth() + 1)}/${timestamp.getFullYear()}`;
              const timePart = timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              return `${datePart} ${timePart}`;
            })()
          };
        })
        .filter(order => order.orderStatus === 'pending');

      const isPickupLater = (o) => (o.pickupOption || o.customerInfo?.pickupOption || '').toLowerCase() === 'later';
      const getScheduledMs = (o) => {
        const dateStr = o.pickupDate || o.customerInfo?.pickupDate;
        const timeStr = o.pickupTime || o.customerInfo?.pickupTime;
        if (!dateStr || !timeStr) return null;
        const dt = new Date(`${dateStr}T${timeStr}`);
        return dt.getTime();
      };

      // Sort: pickup now first (oldest first), then pickup later by scheduled date/time
      const pickupNow = [];
      const pickupLater = [];

      filtered.forEach((order) => {
        if (isPickupLater(order)) {
          pickupLater.push(order);
        } else {
          pickupNow.push(order);
        }
      });

      pickupNow.sort((a, b) => a.timestamp - b.timestamp);

      pickupLater.sort((a, b) => {
        const aMs = getScheduledMs(a) ?? a.timestamp.getTime();
        const bMs = getScheduledMs(b) ?? b.timestamp.getTime();
        if (aMs !== bMs) return aMs - bMs;
        return a.timestamp - b.timestamp;
      });

      const sorted = [...pickupNow, ...pickupLater];

      // compute ids in the same order
      const newIds = sorted.map(o => o.id);

      // Determine if there are any truly new IDs compared to lastPreorderIds
      const hasNew = newIds.some(id => !lastPreorderIds.includes(id));
      if (hasNew) {
        // pick the newest order id (last element after ascending sort)
        const newest = filtered.length ? filtered[filtered.length - 1].id : null;
        if (newest && !lastPreorderIds.includes(newest)) {
          // notify once for the newest order
          toast.success('New queue order received!');
          playSound();

          setShowNewOrder(prev => ({ ...prev, [newest]: true }));

          const timer = setTimeout(() => clearNewOrder(newest), 30000);
          setNewOrderTimers(prev => ({ ...prev, [newest]: timer }));
        }
      }

      setLastPreorderIds(newIds);
      setPreorders(sorted);
    });
  };

  const todayStr = useMemo(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  }, []);

  const filteredPreorders = useMemo(() => {
    return preorders.filter((order) => {
      const pickupOption = (order.pickupOption || order.customerInfo?.pickupOption || '').toLowerCase();
      const pickupDate = order.pickupDate || order.customerInfo?.pickupDate || '';

      if (filter === 'today') {
        return pickupOption === 'now' || (pickupOption === 'later' && pickupDate === todayStr);
      }
      if (filter === 'later') {
        return pickupOption === 'later' && pickupDate !== todayStr;
      }
      if (filter === 'preorder') {
        return pickupOption === 'preorder';
      }
      return true;
    });
  }, [filter, preorders, todayStr]);

  const orderCounts = useMemo(() => {
    const totals = { today: 0, later: 0, preorder: 0 };

    preorders.forEach((order) => {
      const pickupOption = (order.pickupOption || order.customerInfo?.pickupOption || '').toLowerCase();
      const pickupDate = order.pickupDate || order.customerInfo?.pickupDate || '';

      if (pickupOption === 'preorder') {
        totals.preorder += 1;
      } else if (pickupOption === 'later' && pickupDate !== todayStr) {
        totals.later += 1;
      } else if (pickupOption === 'now' || (pickupOption === 'later' && pickupDate === todayStr)) {
        totals.today += 1;
      }
    });

    return totals;
  }, [preorders, todayStr]);

  const exportPreordersToCsv = () => {
    const rows = preorders.filter(
      (order) => (order.pickupOption || order.customerInfo?.pickupOption || '').toLowerCase() === 'preorder'
    );

    if (rows.length === 0) {
      toast.error('No pre-order data to export.');
      return;
    }

    // Gather dynamic option columns across all pre-orders
    const optionLabels = new Set();
    const getOptionEntries = (item) => {
      if (Array.isArray(item.selectedOptions) && item.selectedOptions.length > 0) {
        return item.selectedOptions.map((opt) => ({
          label: (opt.optionLabel || opt.optKey || opt.name || opt.label || 'Option').split(':')[0].trim(),
          value: opt.valueLabel || opt.valueKey || '',
        }));
      }
      const variantStr = item.variantName || item.variantId;
      if (variantStr && typeof variantStr === 'string' && variantStr.includes(':')) {
        return variantStr.split('/').map((seg) => {
          const [rawLabel, rawValue] = seg.split(':');
          return {
            label: (rawLabel || '').trim() || 'Variant',
            value: (rawValue || '').trim(),
          };
        });
      }
      return [];
    };

    rows.forEach((order) => {
      (order.items || []).forEach((item) => {
        getOptionEntries(item).forEach(({ label }) => {
          if (label) optionLabels.add(label);
        });
      });
    });

    const optionColumns = Array.from(optionLabels);

    const headers = [
      'Order ID',
      'Queue Number',
      'Customer Name',
      'Phone',
      'Item',
      'Quantity',
      ...optionColumns,
    ];
    const csvLines = [
      headers.join(','),
      ...rows.map((order) => {
        return (order.items || []).map((item) => {
          const children =
            item.type === 'bundle' && Array.isArray(item.items)
              ? ` [${item.items.map((b) => `${b.name}${b.variantName ? ` (${b.variantName})` : ''} x${b.quantity || b.qty || 1}`).join('; ')}]`
              : '';
          const itemName = `${item.name || ''}${children}`;
          const baseCells = [
            order.id,
            order.queueNumber ?? '',
            `"${(order.customerName || order.customerInfo?.name || '').replace(/"/g, '""')}"`,
            `"${(order.customerPhone || order.customerInfo?.phone || '').replace(/"/g, '""')}"`,
            `"${itemName.replace(/"/g, '""')}"`,
            item.quantity || item.qty || 1,
          ];

          const entries = getOptionEntries(item);
          const optionValues = optionColumns.map((colLabel) => {
            const match = entries.find((entry) => entry.label === colLabel);
            if (match) return `"${String(match.value || '').replace(/"/g, '""')}"`;
            return '""';
          });

          return [...baseCells, ...optionValues].join(',');
        }).join('\n');
      }),
    ];

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'preorders.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Pre-orders exported');
  };


  useEffect(() => {
    if (!user) return;

    const unsubPre = listenToPreorders();

    return () => {
      unsubPre();
    };
  }, [user]);

  const playSound = () => {
    const audio = new Audio('/sounds/notification.mp3');
    audio.play().catch(err => console.warn("Audio play failed:", err));
  };

  const handleCompletePreorder = async (order) => {
    const ref = doc(db, `companies/${companyId}/preorders/${order.id}`);
    await updateDoc(ref, { 
      orderStatus: 'completed', 
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    // Dispatch event to show announcement in QueueDisplay
    const event = new CustomEvent('orderCompleted', {
      detail: {
        queueNumber: order.queueNumber,
      customerName: order.customerName || 'Customer'
    }
    });
    window.dispatchEvent(event);
  };

  const handleCancelPreorder = async (order) => {
    if (!companyId) return;
    const paymentStatus = (order.paymentStatus || '').toLowerCase();
    const paymentMethod = (order.paymentMethod || '').toLowerCase();
    const needsRefund = paymentStatus === 'paid' && paymentMethod.includes('stripe');
    const confirmMessage = needsRefund
      ? 'Cancel this order and restore inventory? A full refund will be issued.'
      : 'Cancel this order and restore inventory?';
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;
    setCancellingId(order.id);
    try {
      if (needsRefund) {
        const refundPayment = httpsCallable(functions, "refundOnlineOrderPayment");
        await refundPayment({ companyId, orderId: order.id });
      }

      // Restore inventory for each item (aggregate to avoid redundant updates)
      const productRestorations = {};
      const parseQty = (value, fallback = 0) => {
        const n = Number(value ?? fallback);
        return Number.isFinite(n) ? n : 0;
      };
      const addRestore = (productId, qtyToRestore, variantId = null) => {
        if (!productId || qtyToRestore <= 0) return;
        const entry = productRestorations[productId] || { qty: 0, variants: {} };
        entry.qty += qtyToRestore;
        if (variantId && variantId !== "__ANY_VARIANT__") {
          entry.variants[variantId] = (entry.variants[variantId] || 0) + qtyToRestore;
        }
        productRestorations[productId] = entry;
      };

      (order.items || []).forEach((item) => {
        const itemQty = parseQty(item.quantity ?? item.qty, 0);
        if (item.type === 'bundle' && Array.isArray(item.items)) {
          item.items.forEach((child) => {
            const childQty = parseQty(child.quantity ?? child.qty, 1);
            addRestore(child.id, childQty * itemQty, child.variantId || null);
          });
          return;
        }
        addRestore(item.id, itemQty, item.variantId || null);
      });

      const inventoryCollection = collection(db, `companies/${companyId}/inventory/data/products`);
      await Promise.all(
        Object.entries(productRestorations).map(([productId, info]) =>
          runTransaction(db, async (tx) => {
            const productRef = doc(inventoryCollection, productId);
            const snap = await tx.get(productRef);
            if (!snap.exists()) return;
            const data = snap.data() || {};

            const currentQty = Number(data.qty ?? data.quantity ?? 0);
            const payload = { qty: currentQty + (info.qty || 0) };

            if (info.variants && Object.keys(info.variants).length > 0 && Array.isArray(data.variants)) {
              const nextVariants = data.variants.map((v) => {
                const vid = v.id || v.variantId;
                const restore = info.variants[vid];
                if (!restore) return v;
                const current = Number(v.qty ?? v.quantity ?? 0);
                return { ...v, qty: current + restore };
              });
              payload.variants = nextVariants;
            }

            tx.update(productRef, payload);
          })
        )
      );

      const voidReason = "customer refund";
      const orderDocRef = doc(db, `companies/${companyId}/orders/${order.id}`);
      const orderDocSnap = await getDoc(orderDocRef);
      if (orderDocSnap.exists()) {
        await updateDoc(orderDocRef, {
          paymentStatus: "Void",
          voidReason,
          voidedAt: new Date().toISOString(),
        });
      }
      if (publicId) {
        const publicRef = doc(db, `publicUsers/${publicId}/receipts/${order.id}`);
        const publicSnap = await getDoc(publicRef);
        if (publicSnap.exists()) {
          await updateDoc(publicRef, {
            paymentStatus: "Void",
            voidReason,
            voidedAt: new Date().toISOString(),
          });
        }
      }

      // Delete preorder
      const preRef = doc(db, `companies/${companyId}/preorders/${order.id}`);
      await deleteDoc(preRef);

      // Delete online order entry (phone bucket)
      const phoneRaw = order.customerInfo?.phone || order.customerPhone || '';
      const phoneKey = phoneRaw.replace(/\s+/g, '');
      if (phoneKey) {
        const onlineRef = doc(db, `companies/${companyId}/onlineOrders/${phoneKey}/data/${order.id}`);
        await deleteDoc(onlineRef);
      }
    } catch (err) {
      console.error('Cancel preorder failed', err);
      toast.error('Failed to cancel order. Please try again.');
    } finally {
      setCancellingId(null);
    }
  };

  const handleVerifyPayment = async (order) => {
    if (!companyId || !order?.id) return;
    setVerifyingId(order.id);
    try {
      const companyDoc = await getDoc(doc(db, `companies/${companyId}`));
      const companyProfileDoc = await getDoc(doc(db, `companies/${companyId}/companyProfile/info`));
      const companySettingsDoc = await getDoc(doc(db, `companies/${companyId}/companySettings/info`));
      const companyProfile = companyProfileDoc.exists() ? companyProfileDoc.data() : {};
      const companySettings = companySettingsDoc.exists() ? companySettingsDoc.data() : {};
      const tenantPublicId = companyDoc.exists() ? companyDoc.data()?.publicId || null : null;
      const totals = order.totals || {};
      const orderTotalValue = Number(totals.total ?? order.total ?? 0);
      const orderSubtotal = Number(totals.subtotal ?? order.subtotal ?? 0);
      const sst6Amount = Number(totals.sst6Amount ?? order.sst6Amount ?? 0);
      const sst8Amount = Number(totals.sst8Amount ?? order.sst8Amount ?? 0);
      const serviceTax10Amount = Number(totals.serviceTax10Amount ?? order.serviceTax10Amount ?? 0);
      const serviceTax10Rate = Number(order.serviceTax10Rate ?? companySettings.serviceTax10Rate ?? 0);
      const discountAmount = Number(totals.discountAmount ?? order.discountAmount ?? 0);
      const receiptNumber = order.orderNumber || order.id;
      const nowIso = new Date().toISOString();
      const cleanedItems = (order.items || []).map((item) => {
        const cleaned = {
          id: item.id || null,
          name: item.name || "",
          price: Number(item.price || 0),
          qty: item.qty ?? item.quantity ?? 1,
          quantity: item.qty ?? item.quantity ?? 1,
          variantId: item.variantId ?? null,
          variantName: item.variantName ?? null,
          type: item.type || "product",
          note: item.note || "",
          variantPriceDelta: Number(item.variantPriceDelta || 0),
          selectedOptions: item.selectedOptions || item.options || [],
        };
        if (item.type === "bundle" && Array.isArray(item.items)) {
          cleaned.items = item.items.map((sub) => ({
            id: sub.id || null,
            name: sub.name || "",
            quantity: sub.quantity || 1,
            variantId: sub.variantId ?? null,
            variantName: sub.variantName ?? null,
            selectedOptions: sub.selectedOptions || sub.options || [],
            variantPriceDelta: Number(sub.variantPriceDelta || 0),
          }));
        }
        return cleaned;
      });

      const orderRef = doc(db, `companies/${companyId}/preorders/${order.id}`);
      await updateDoc(orderRef, {
        paymentStatus: 'paid',
        paymentMethod: 'DuitNow QR',
        paymentProvider: 'duitnow_qr',
        paymentVerifiedAt: new Date().toISOString(),
      });

      const orderDocRef = doc(db, `companies/${companyId}/orders/${order.id}`);
      await setDoc(orderDocRef, {
        customerName: order.customerName || order.customerInfo?.name || "",
        customerPhone: order.customerPhone || order.customerInfo?.phone || "",
        receiptNumber,
        queueNumber: order.queueNumber ?? null,
        mode: order.mode || "PICKUP",
        orderMode: order.orderMode || "ONLINE",
        paymentMethod: "DuitNow QR",
        paymentProvider: "duitnow_qr",
        items: cleanedItems,
        subtotal: orderSubtotal,
        sst6Amount,
        sst8Amount,
        serviceTax10Amount,
        serviceTax10Rate,
        discount: order.discount || "-",
        discountAmount,
        rounding: Number(order.rounding || 0).toFixed(2),
        total: orderTotalValue,
        received: orderTotalValue,
        balance: 0,
        timestamp: nowIso,
        paymentStatus: "Paid",
        cashierId: "online",
        orderType: order.orderType || null,
        fulfillmentType: order.fulfillmentType || order.customerInfo?.fulfillmentType || "pickup",
        deliveryAddress: order.deliveryAddress || order.customerInfo?.deliveryAddress || "",
        pickupOption: order.pickupOption || null,
        pickupDate: order.pickupDate || null,
        pickupTime: order.pickupTime || null,
      }, { merge: true });

      if (tenantPublicId) {
        await setDoc(doc(db, `publicUsers/${tenantPublicId}/receipts/${order.id}`), {
          companyLogo: companyProfile.logoUrl,
          companyName: companyProfile.companyName,
          companyStreet: companyProfile.street,
          companyCity: companyProfile.city,
          companyPostcode: companyProfile.postcode,
          companyState: companyProfile.state,
          companyCountry: companyProfile.country,
          companyCountryCode: companyProfile.countryCode,
          companyPhone: companyProfile.phone,
          companyWebsite: companyProfile.website,
          companySSTNumber: companyProfile.sstNumber,
          companyRegType: companyProfile.regType || "",
          companyRegNumber: companyProfile.regNumber || "",
          receiptNumber,
          queueNumber: order.queueNumber ?? null,
          mode: order.mode || "PICKUP",
          paymentMethod: "DuitNow QR",
          items: cleanedItems,
          subtotal: orderSubtotal,
          sst6Amount,
          sst8Amount,
          serviceTax10Amount,
          serviceTax10Rate,
          discount: order.discount || "-",
          discountAmount,
          rounding: Number(order.rounding || 0).toFixed(2),
          total: orderTotalValue,
          received: orderTotalValue,
          balance: 0,
          timestamp: nowIso,
          paymentStatus: "Paid",
          cashierId: "online",
          footer: companySettings.receiptFooter,
          thankYouNote: companySettings.thankYouNote,
        }, { merge: true });
      }
      toast.success('QR payment verified.');
    } catch (error) {
      console.error('Failed to verify payment:', error);
      toast.error('Failed to verify payment.');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleRejectPayment = async (order) => {
    if (!companyId || !order?.id) return;
    const confirmed = window.confirm('Reject this payment proof? The uploaded proof will be deleted and the customer must pay again.');
    if (!confirmed) return;
    setRejectingId(order.id);
    try {
      if (order.paymentProofPath) {
        try {
          await deleteObject(storageRef(storage, order.paymentProofPath));
        } catch (storageError) {
          console.warn('Failed to delete payment proof file:', storageError);
        }
      }

      const orderRef = doc(db, `companies/${companyId}/preorders/${order.id}`);
      await updateDoc(orderRef, {
        paymentStatus: 'proof_rejected',
        paymentMethod: 'Payment Required',
        paymentProvider: '',
        paymentProofUrl: '',
        paymentProofName: '',
        paymentProofContentType: '',
        paymentProofPath: '',
        paymentProofUploadedAtIso: '',
        paymentRejectedAt: new Date().toISOString(),
      });
      toast.success('Payment proof rejected. Customer must pay again.');
    } catch (error) {
      console.error('Failed to reject payment:', error);
      toast.error('Failed to reject payment.');
    } finally {
      setRejectingId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 min-h-screen">
      <Toaster position="top-right" toastOptions={{
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151',
          fontSize: '1.1rem',
          padding: '1rem'
        }
      }} />
      
    <div className="mx-auto">
      <div className="flex items-center mb-6">
        <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">
          {stallType === 'fnb' ? 'KITCHEN DISPLAY' : 'ONLINE ORDER DISPLAY'}
        </h2>
      </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Queue Orders */}
          <div className="bg-gray-800 rounded-xl p-5 border-2 border-gray-700 shadow-xl">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 mb-4">
              <div className="flex items-center">
                <FiClock className="text-2xl text-yellow-400 mr-3" />
                <h3 className="text-2xl font-bold text-gray-100">QUEUE ORDERS</h3>
              </div>
              <div className="flex items-center flex-wrap gap-2 md:ml-auto">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'later', label: 'Pickup Later' },
                  { key: 'preorder', label: 'Pre-order' },
                ].map((btn) => (
                  <button
                    key={btn.key}
                    onClick={() => setFilter(btn.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                      filter === btn.key
                        ? 'bg-yellow-500 text-gray-900'
                        : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    }`}
                  >
                    <span>{btn.label}</span>
                    <span
                      className={`ml-2 inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-xs font-bold ${
                        filter === btn.key
                          ? 'bg-gray-900 text-yellow-300'
                          : 'bg-gray-600 text-gray-100'
                      }`}
                    >
                      {orderCounts[btn.key] ?? 0}
                    </span>
                  </button>
                ))}
                <button
                  onClick={exportPreordersToCsv}
                  className="px-3 py-1.5 rounded-full text-sm font-semibold bg-green-600 hover:bg-green-500 text-white transition-colors"
                >
                  Export Pre-orders
                </button>
              </div>
            </div>
            
            {filteredPreorders.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <FiClock className="mx-auto text-5xl mb-4 opacity-30" />
                <p className="text-xl">No pending queue orders</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPreorders.map((order, index) => {
                  const isNew = index === 0; // Only the first (most recent) order is considered new
                  const pickupDate = order.pickupDate || order.customerInfo?.pickupDate;
                  const pickupTime = order.pickupTime || order.customerInfo?.pickupTime;
                  const pickupOption = order.pickupOption || order.customerInfo?.pickupOption;
                  return (
                    <div 
                      key={order.id} 
                      className={`bg-gray-750 border-l-4 ${isNew ? 'border-yellow-400 animate-pulse' : 'border-yellow-500'} rounded-r-xl overflow-hidden`}
                      style={isNew ? { '--tw-pulse-duration': '20s' } : {}}
                    >
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center flex-wrap gap-2">
                              <FiHash className="text-yellow-400 text-xl" />
                              <span className="font-bold text-2xl text-yellow-300">
                                {String(order.queueNumber).padStart(3, '0')}
                              </span>
                              {order.fromOnline && (
                                <span className="text-sm bg-green-600/90 text-white px-3 py-1 rounded-full font-semibold">
                                  ONLINE
                                </span>
                              )}
                              {order.fromOnline && (
                                <span
                                  className={`text-sm px-3 py-1 rounded-full font-semibold ${
                                    (order.paymentStatus || '').toLowerCase() === 'paid'
                                      ? 'bg-emerald-600/90 text-white'
                                      : 'bg-red-600/90 text-white'
                                  }`}
                                >
                                  {(order.paymentStatus || 'unpaid').toUpperCase()}
                                </span>
                              )}
                            </div>
                            {order.customerName && (
                              <div className="flex items-center mt-2 text-lg text-gray-300">
                                <FiUser className="mr-2 text-base" />
                                {order.customerName}
                              </div>
                            )}
                            {(order.customerPhone || order.customerInfo?.phone) && (
                              <div className="flex items-center mt-1 text-base text-gray-400">
                                <FiPhone className="mr-2 text-sm" />
                                {order.customerPhone || order.customerInfo?.phone}
                              </div>
                            )}
                            {order.orderType && (
                              <div className="flex items-center mt-1 text-base text-blue-300">
                                <span className="mr-2">🍽️</span>
                                <span className="font-medium">
                                  {order.orderType === 'dineIn'
                                    ? 'DINE IN'
                                    : order.orderType === 'delivery'
                                    ? 'DELIVERY'
                                    : 'TAKE AWAY'}
                                </span>
                              </div>
                            )}
                            {(pickupOption === 'now' || pickupOption === 'preorder' || pickupDate || pickupTime) && (
                              <div className="flex items-center mt-1 text-base text-green-300">
                                <FiClock className="mr-2 text-sm" />
                                <span>
                                  {pickupOption === 'now'
                                    ? `${order.fulfillmentType === 'delivery' ? 'Delivery' : 'Pickup'}: Now`
                                    : pickupOption === 'preorder'
                                    ? `${order.fulfillmentType === 'delivery' ? 'Delivery' : 'Pickup'}: Scheduled`
                                    : `${order.fulfillmentType === 'delivery' ? 'Delivery' : 'Pickup'}: ${pickupDate || '—'}${pickupDate && pickupTime ? ' • ' : ' '}${pickupTime || ''}`}
                                </span>
                              </div>
                            )}
                            {order.fulfillmentType === 'delivery' && (order.deliveryAddress || order.customerInfo?.deliveryAddress) && (
                              <div className="mt-1 text-base text-orange-300">
                                Delivery Address: {order.deliveryAddress || order.customerInfo?.deliveryAddress}
                              </div>
                            )}
                            {order.paymentProofUrl && (
                              <div className="mt-1">
                                <a
                                  href={order.paymentProofUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm text-cyan-300 underline hover:text-cyan-200"
                                >
                                  View Uploaded Receipt
                                </a>
                              </div>
                            )}
                            <div className="text-base text-gray-400 mt-2">
                              {order.displayTime}
                            </div>
                          </div>
                          {String(order.paymentStatus || '').toLowerCase() === 'pending_verification' && (
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                onClick={() => handleVerifyPayment(order)}
                                disabled={verifyingId === order.id}
                                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-md transition-colors disabled:opacity-60"
                              >
                                {verifyingId === order.id ? 'Verifying...' : 'Verify Payment'}
                              </button>
                              <button
                                onClick={() => handleRejectPayment(order)}
                                disabled={rejectingId === order.id}
                                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-md transition-colors disabled:opacity-60"
                              >
                                {rejectingId === order.id ? 'Rejecting...' : 'Reject Payment'}
                              </button>
                            </div>
                          )}
                        </div>

                        <ul className="mt-4 space-y-3 border-t border-gray-700 pt-4">
                          {order.items.map((item, idx) => (
                            <li key={idx} className="text-lg">
                              <div className="flex items-start">
                                <div>
                                  <span className="font-bold text-gray-100">
                                    {item.name.toUpperCase()} × {item.qty}
                                  </span>
                                  {item.variantName && (
                                    <div className="text-sm text-gray-300 font-medium mt-0.5">
                                      Variant: {item.variantName}
                                    </div>
                                  )}
                                </div>
                                {item.type === 'bundle' && (
                                  <span className="ml-3 text-sm bg-purple-600/90 text-white px-3 py-1 rounded-full">
                                    BUNDLE
                                  </span>
                                )}
                              </div>
                              {item.note && (
                                <div className="text-base text-yellow-300 italic mt-1">
                                  Note: {item.note}
                                </div>
                              )}
                              {item.type === 'bundle' && item.items && (
                                <div className="ml-6 pl-4 mt-2 border-l-2 border-gray-600 space-y-2">
                                  {item.items.map((bundleItem, bIdx) => (
                                    <div key={bIdx} className="text-base text-gray-400 flex justify-between">
                                      <span className="flex-1">
                                        <span className="text-gray-200 font-medium">{bundleItem.name}</span>
                                        {bundleItem.variantName && (
                                          <span className="text-sm text-gray-300 block">
                                            Variant: {bundleItem.variantName}
                                          </span>
                                        )}
                                      </span>
                                      <span className="ml-3 text-gray-300 font-bold">{bundleItem.quantity}x</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>

                        <div className="mt-4 flex flex-col gap-3 border-t border-gray-700 pt-4">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleCompletePreorder(order)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-bold rounded-lg transition-colors"
                            >
                              <FiCheckCircle className="text-base" />
                              COMPLETE
                            </button>
                            <button
                              onClick={() => handleCancelPreorder(order)}
                              disabled={cancellingId === order.id}
                              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-60"
                            >
                              {cancellingId === order.id ? 'CANCELLING...' : 'CANCEL'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
